/*
 * © 2017 Renishaw plc. All rights reserved.
 * This source file is the confidential property and copyright of Renishaw plc
 * Reproduction or transmission in whole or in part, in any form or
 * by any means, electronic, mechanical or otherwise, is prohibited
 * without the prior written consent of the copyright owner.
 */
/* eslint-disable camelcase */
import "core-js";
import "regenerator-runtime/runtime";
import React from "react";
import * as ReactDOMClient from "react-dom/client";
import App from "./App";
import { setBuildNumber, initI18n } from "@/i18n";
import { App as Error } from "./App/Error";
import axios from "axios";
import { setBase } from "./axios";
import { startStore, store, rootActions } from "./store";
import defaultConfig from "@/config/index.json";
import defaultBuildConfig from "@/config/buildinfo.json";
import { updateLocale } from "@centralwebteam/jellyfish";
import i18n from "i18next";
import {
  sanitizeBuildNumber,
  isAuthFailure,
  AuthResponse,
} from "@centralwebteam/narwhal";
import { CMSClient } from "@/cms-api/";
import constants from "./constants";

export type Config = typeof defaultConfig;
let config: Config = defaultConfig;
let buildConfig: typeof defaultBuildConfig = defaultBuildConfig;
let apiBase = "";

// These config getters cause problems in some situations, such as in selectors and in workers.
// If you need configured values there, you need to either inject them in props or use the store.
// Add to the reducer for the `configLoaded` action if you need more config in the store.
export const getBaseURL = () => apiBase;
export const getGlobalConfigs = () => config.globalConfigs;
export const getBuildConfigs = () => buildConfig;

async function start() {
  const container = document.getElementById("app");
  try {
    buildConfig = await loadBuildConfig();
    setBuildNumber(buildConfig.buildnumber);
    await initI18n();
    config = await loadConfig();
    apiBase = config.api || "../api/";
    setBase(apiBase);
    updateLocale(i18n.language);
    startStore();

    // Handle tab sleeping (e.g., Edge's aggressive tab sleep feature)
    // When tab wakes up, check if token needs refresh
    setupVisibilityChangeHandler();

    ReactDOMClient.createRoot(container!).render(<App config={config} />);
  } catch (error: any) {
    console.error(error);
    // Build server throws this error from unit test "Renders error message if status change is in the future".
    if (!container || error.message?.includes("Target container")) return;
    ReactDOMClient.createRoot(container).render(<Error />);
  }
}

start();

async function loadConfig() {
  try {
    const base = window.location.href.split(/[#?]/)[0];
    const sanitizedBuildNumber = sanitizeBuildNumber(
      buildConfig.buildnumber || "unknown"
    );
    return await axios
      .get(`${base}config/index.json?v=${sanitizedBuildNumber}`)
      .then((res) => res.data);
  } catch (error) {
    console.log("Error", error);
    // This happens in unit test context
    return defaultConfig;
  }
}

async function loadBuildConfig() {
  try {
    const base = window.location.href.split(/[#?]/)[0];
    return await axios
      .get(`${base}config/buildinfo.json?v=${Date.now()}`)
      .then((res) => res.data);
  } catch (error) {
    console.log("Error", error);
    return { buildNumber: "unknown" };
  }
}

// We have promise cancellation which won't neccesarily get handled.
// Match on the event.reason.message if it contains "cancelled" supress it (we don't care!).
window.addEventListener("unhandledrejection", function (promiseRejectionEvent) {
  if (
    promiseRejectionEvent.reason.message &&
    !promiseRejectionEvent.reason.message.toLowerCase().includes("cancelled")
  )
    console.error(promiseRejectionEvent);
  else promiseRejectionEvent.preventDefault();
});

/**
 * Handle page visibility changes to detect when tab wakes from sleep.
 * If the token is expired or about to expire, trigger a proactive refresh
 * before any API calls are made. This prevents 401 errors when Edge's
 * aggressive tab sleeping prevents the scheduled saga refresh from running.
 */
function setupVisibilityChangeHandler() {
  let visibilityRefreshing: Promise<AuthResponse> | null = null;

  document.addEventListener("visibilitychange", async () => {
    if (document.visibilityState === "visible") {
      try {
        const authData = localStorage.getItem(constants.authLocalStorageKey);
        if (!authData) return;

        const auth = JSON.parse(authData);
        const now = Date.now();
        const expiryTime = auth.expiry || 0;
        const secondsUntilExpiry = Math.floor((expiryTime - now) / 1000);

        // If token expires in less than 2 minutes, proactively refresh it
        if (secondsUntilExpiry < 120) {
          console.log(
            `Tab woke up. Token expires in ${secondsUntilExpiry}s. Triggering proactive refresh.`
          );

          // If already refreshing, don't start another refresh
          if (window.isRefreshing || visibilityRefreshing !== null) {
            console.log(
              "Refresh already in progress, skipping proactive refresh"
            );
            return;
          }

          try {
            window.isRefreshing = true;

            // Refresh the token using the same approach as the saga
            visibilityRefreshing = CMSClient.auth.refresh({
              grant_type: "refresh_token",
              client_id: constants.clientId,
              refresh_token: auth.refresh_token,
            }).promise;

            const refreshResponse = await visibilityRefreshing;

            if (isAuthFailure(refreshResponse)) {
              console.warn(
                `Proactive refresh failed: ${refreshResponse.error}`
              );
              // Let the axios interceptor handle it on next API call
              return;
            }

            // Update localStorage with the new token
            const expiry = Date.now() + refreshResponse.expires_in * 1000;
            localStorage.setItem(
              constants.authLocalStorageKey,
              JSON.stringify({
                ...refreshResponse,
                username: auth.username,
                expiry,
              })
            );

            // Dispatch to saga to schedule next refresh
            store.dispatch(
              rootActions.session.userSessionRefreshed(refreshResponse)
            );

            console.log("Proactive refresh successful");
          } finally {
            window.isRefreshing = false;
            visibilityRefreshing = null;
          }
        }
      } catch (error) {
        // Ignore errors in visibility handler - axios interceptor will handle token issues
        console.warn("Error in proactive token refresh:", error);
        window.isRefreshing = false;
        visibilityRefreshing = null;
      }
    }
  });
}
